import { connect } from 'react-redux';
import { concat, defaultTo, first, isEqual, last } from 'lodash';

import { SelectionOverride, FilterSelection } from 'src/common-ui/components/Filters/Filters';

import serviceContainer from 'src/ServiceContainer';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { DaysRangeListResponse, FilterGroup } from 'src/types/Scope';
import { computeFilterSelection } from 'src/utils/Filter/Filters';
import { applyAttributeBlacklistToFilterGroups } from 'src/utils/Pivot/Filter';
import FilterPanel from './FilterPanel';

import {
  updateSelectionOverrides,
  flushSelectionOverridesStarted,
  receiveFilterState,
  makeUpdateLastSelection,
  receiveFilterStateAfterSelection,
  isLoading,
  setLoadingSectionId,
  rollbackSelectionOverrides,
  receiveFilterStateAfterSubmission,
  overwriteSelectionOverridesByDefnId,
  clearAllSelections,
  clearTabSelections,
  clearSectionSelections,
} from './FilterPanel.slice';
import { toast } from 'react-toastify';
import {
  getActiveContext,
  getActiveContextString,
} from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import type { ContextFilterConfig } from 'configuration-schemas/confdefn';
import {
  getAttributeBlacklist,
  getCurrentDepartment,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.selectors';

export interface FilterSelections {
  id: string;
  filterDefnId: string;
}

export interface ReduxSlice {
  isFilterSensitive?: boolean;
  updatedAt?: number;
  state: FilterGroup[];
  isFlushing: boolean;
  isLoading: boolean;
  loadingSectionId: string | null;
  selectionOverrides: SelectionOverride[];
  lastSelections: FilterSelection[];
  lastPostedSelections: FilterSelection[];
  containsInvalid: boolean;
  selectionsChanged: boolean;
}

export interface StoreProps extends ReduxSlice {
  allowFrom: string;
  allowTo: string;
  daysRangeList: DaysRangeListResponse;
  scopeStartMonth: string | null;
  contextFilterConfig?: ContextFilterConfig;
}
export interface Props extends DispatchProps, StoreProps {}

export function submitFilterSelections() {
  return (dispatch: AppThunkDispatch, getState: () => AppState): Promise<void> => {
    const state = getState();
    const storeProps = state.filters;
    const perspective = state.perspective.selected!;
    const appName = perspective.appType;
    const filterSelections = computeFilterSelection(storeProps.state, storeProps.selectionOverrides);
    const section = getActiveContextString(state);
    if (!isEqual(filterSelections, storeProps.lastSelections)) {
      dispatch(flushSelectionOverridesStarted());
      return serviceContainer.filterClient
        .setFilterSelections(filterSelections, {
          appName,
          section,
        })
        .then(() => {
          return serviceContainer.filterClient
            .getFullFilters({
              appName,
              section,
            })
            .then((refreshResult) => {
              if (refreshResult) {
                const submission = {
                  filters: refreshResult.filters,
                  updatedAt: Date.now(),
                };
                dispatch(receiveFilterStateAfterSubmission(submission));
                dispatch(makeUpdateLastSelection(filterSelections));
              }
            });
        })
        .catch((err) => {
          // toast here incase of submission failure as well
          toast.error('An error occured submitting filter selections');
          serviceContainer.loggingService.error('An error occured submitting filter selections', err);
        });
    }
    // if the filters are equal, then we still want to return a resolved promise,
    // otherwise we'd return undef and the return shape would be wrong
    return Promise.resolve();
  };
}
export function getAvailableFilterSelections(sectionId: string) {
  return (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const state = getState();
    const storeProps = state.filters;
    const perspective = state.perspective.selected!;
    const appName = perspective.appType;
    const filterSelections = computeFilterSelection(storeProps.state, storeProps.selectionOverrides);
    const section = getActiveContextString(state);
    dispatch(setLoadingSectionId(sectionId));
    serviceContainer.filterClient
      .getAvailableFilterSelections({
        appName,
        section,
        filterSelections,
        attributeId: sectionId,
      })
      .then((refreshResult) => {
        if (refreshResult) {
          dispatch(
            receiveFilterStateAfterSelection({
              filters: refreshResult.filters,
              postedSelections: filterSelections,
            }),
          );
        }
      })
      .catch((err) => {
        // dispatch so not stuck in loading
        dispatch(
          receiveFilterStateAfterSelection({
            filters: storeProps.state, // set to the filters that were there before trying to run this
            postedSelections: filterSelections,
          }),
        );
        // toast here so the error is known
        toast.error('An error occured fetching available filter selections');
        serviceContainer.loggingService.error('An error occured fetching filters after selection', err);
      });
  };
}
export function getFilters() {
  return (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const state = getState();
    const perspective = state.perspective.selected!;
    const appName = perspective.appType;
    const section = getActiveContextString(state);

    dispatch(flushSelectionOverridesStarted());
    dispatch(isLoading(true));
    return serviceContainer.filterClient
      .getFullFilters({
        appName,
        section,
      })
      .then((result) => {
        if (result) {
          const filters = applyAttributeBlacklistToFilterGroups(
            result.filters,
            getAttributeBlacklist(state),
            getCurrentDepartment(state) ?? undefined,
          );
          dispatch(receiveFilterState({ filters, updatedAt: Date.now() }));
        }
      })
      .catch((err) => {
        dispatch(receiveFilterState({ filters: [], updatedAt: Date.now() }));
        toast.error('An error occured fetching filters');
        serviceContainer.loggingService.error('An error occured fetching filters', err);
      });
  };
}

export function getFilterSelections() {
  return (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const state = getState();
    const perspective = state.perspective.selected!;
    const appName = perspective.appType;
    serviceContainer.filterClient.getOnlyFilterSelections(appName).then((selections) => {
      dispatch(rollbackSelectionOverrides(selections));
    });
  };
}

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    updateSelectionOverrides(overrides: SelectionOverride[]) {
      dispatch(updateSelectionOverrides(overrides));
    },
    overwriteSelectionOverridesByDefnId(defnId: string, selections: SelectionOverride[]) {
      dispatch(overwriteSelectionOverridesByDefnId({ defnId, selections }));
    },
    clearAllSelections() {
      dispatch(clearAllSelections());
    },
    clearTabSelections(tabId: string) {
      dispatch(clearTabSelections(tabId));
    },
    clearSectionSelections(sectionId: string) {
      dispatch(clearSectionSelections(sectionId));
    },
    submitSelectionOverrides(): Promise<void> {
      return dispatch(submitFilterSelections());
    },
    getAvailableFilterSelections(sectionId: string) {
      dispatch(getAvailableFilterSelections(sectionId));
    },
  };
};

const mapStateToProps = (state: AppState) => {
  const { filters, scope } = state;
  const { daysRangeList, daysPastRangeList, pastRangeList, rangeListExtended } = scope;

  const filterSelections = computeFilterSelection(filters.state, filters.selectionOverrides);

  // data for range pickers
  // FIXME: should combined lists be in a selector?
  const combinedDaysRangeList: DaysRangeListResponse = {
    start_date: {
      ...daysRangeList.start_date,
      ...daysPastRangeList.start_date,
    },
    end_date: {
      ...daysRangeList.end_date,
      ...daysPastRangeList.end_date,
    },
  };
  const combinedRangeList = concat(pastRangeList, rangeListExtended);
  const startRangeList = combinedRangeList.filter(
    (x) => Object.values(combinedDaysRangeList.start_date).indexOf(x.id) !== -1,
  );
  const endRangeList = combinedRangeList.filter(
    (x) => Object.values(combinedDaysRangeList.end_date).indexOf(x.id) !== -1,
  );
  const allowFrom = defaultTo(first(startRangeList)?.id, '');
  const allowTo = defaultTo(last(endRangeList)?.id, '');

  const activeContext = getActiveContext(state);

  return {
    ...filters,
    selectionsChanged: !isEqual(filterSelections, filters.lastSelections),
    allowFrom,
    allowTo,
    daysRangeList: combinedDaysRangeList,
    scopeStartMonth: scope.scope.start,
    contextFilterConfig: activeContext?.filters,
  };
};

export interface DispatchProps extends ReturnType<typeof mapDispatchToProps> {}

export default connect(mapStateToProps, mapDispatchToProps)(FilterPanel);
